import React, { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import "./UserProfileUpcomingCourses.css";

const STATUS_META = {
    upcoming: { label: "Upcoming", className: "upcoming" },
    pending: { label: "Pending", className: "pending" },
    progress: { label: "In Progress", className: "progress" },
    "in-progress": { label: "In Progress", className: "progress" },
    "in_progress": { label: "In Progress", className: "progress" },
    inprogress: { label: "In Progress", className: "progress" },
    completed: { label: "Completed", className: "completed" },
};

const normalizeStatus = (status) => {
    if (!status) return "upcoming";
    return status.toLowerCase().replace(/\s+/g, "_");
};

const StatusBadge = ({ status }) => {
    const normalizedStatus = normalizeStatus(status);
    const meta = STATUS_META[normalizedStatus] || STATUS_META.upcoming;
    return <span className={`badge ${meta.className}`}>{meta.label}</span>;
};

const EmptyState = ({ title, description }) => (
    <div className="training-empty-card">
        <div className="empty-card-icon-wrapper">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="empty-card-svg"
            >
                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
            </svg>
        </div>
        <h3 className="empty-card-title">{title}</h3>
        <p className="empty-card-description">{description}</p>
    </div>
);

const TrainingItem = ({ item, type }) => (
    <div className="training-item">

        <div className="training-content">
            <div className="training-heading">
                <a
                    href={item.link || "#"}
                    className="training-link"
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: "none" }}
                >
                    {item.title}
                </a>

                {item.certificate && (
                    <a
                        href={item.certificate}
                        target="_blank"
                        rel="noreferrer"
                        className="certificate-pill"
                        style={{ textDecoration: "none", cursor: "pointer" }}
                        onClick={(e) => {
                            e.preventDefault();
                            window.open(item.certificate, "_blank", "noopener,noreferrer");
                        }}
                    >
                        Certificate
                    </a>
                )}
            </div>

            <p className="training-description">{item.description || "No description available."}</p>

            <div className="training-meta">
                <StatusBadge status={item.status} />
                <span className="training-due">
                    {item.progress > 0 ? `${item.progress}% complete` : ""}
                </span>
            </div>

            {item.progress > 0 && (
                <div className="progress-bar" aria-hidden="true">
                    <div className="progress-fill" style={{ width: `${Math.min(item.progress, 100)}%` }} />
                </div>
            )}
        </div>
    </div>
);

const TrainingList = ({ items, type }) => {
    if (!items || items.length === 0) {
        return <p className="training-empty">No {type}s found.</p>;
    }

    return (
        <div className="training-list">
            {items.map((item, index) => (
                <TrainingItem key={`${item.title}-${index}`} item={item} type={type} />
            ))}
        </div>
    );
};

const StatusTabContent = ({ courses, quizzes, surveys, statusFilter }) => {
    const filteredCourses = courses.filter((c) => normalizeStatus(c.status) === normalizeStatus(statusFilter));
    const filteredQuizzes = quizzes.filter((q) => normalizeStatus(q.status) === normalizeStatus(statusFilter));
    const filteredSurveys = surveys.filter((s) => normalizeStatus(s.status) === normalizeStatus(statusFilter));

    const hasCourses = filteredCourses.length > 0;
    const hasQuizzes = filteredQuizzes.length > 0;
    const hasSurveys = filteredSurveys.length > 0;

    if (!hasCourses && !hasQuizzes && !hasSurveys) {
        let title = "No Training Found";
        let description = "There are no training items for this status.";
        if (statusFilter === "completed") {
            title = "No Completed Training";
            description = "You haven't completed any training items yet. Once completed, they will appear here.";
        } else if (statusFilter === "inprogress" || statusFilter === "in_progress" || statusFilter === "in-progress") {
            title = "No In-Progress Training";
            description = "You don't have any active training items.";
        } else if (statusFilter === "upcoming") {
            title = "No Upcoming Training";
            description = "You have no upcoming training scheduled. You are all caught up!";
        }
        return <EmptyState title={title} description={description} />;
    }

    return (
        <div className="status-tab-content">
            {hasCourses && (
                <div className="content-section">
                    <h4 className="section-title">Courses</h4>
                    <TrainingList items={filteredCourses} type="course" />
                </div>
            )}

            {hasQuizzes && (
                <div className="content-section">
                    <h4 className="section-title">Quizzes</h4>
                    <TrainingList items={filteredQuizzes} type="quiz" />
                </div>
            )}

            {hasSurveys && (
                <div className="content-section">
                    <h4 className="section-title">Surveys</h4>
                    <TrainingList items={filteredSurveys} type="survey" />
                </div>
            )}
        </div>
    );
};

export default function UserProfileUpcomingCourses({ userId }) {
    const [courses, setCourses] = useState([]);
    const [quizzes, setQuizzes] = useState([]);
    const [surveys, setSurveys] = useState([]);
    const [activeStatusTab, setActiveStatusTab] = useState("completed");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [emptyStateMessage, setEmptyStateMessage] = useState("");

    useEffect(() => {
        const fetchTrainingData = async () => {
            setLoading(true);
            setError("");

            try {
                const baseUrl = Cookies.get("apiEnv") || "";

                const loggedInUserId = Cookies.get("userId");
                const finalUserId = userId || loggedInUserId;

                const payload = finalUserId ? { user_id: finalUserId } : {};

                const response = await axios.post(`${baseUrl}/tm_user_info/user_courses`, payload);

                const responseData = response?.data?.data || {};
                const responseDataMessage = response?.data || {};

                if (responseDataMessage?.success === false) {
                    setEmptyStateMessage(
                        responseDataMessage?.message || "No training data found."
                    );

                    setCourses([]);
                    setQuizzes([]);
                    setSurveys([]);
                    return;
                }
                setCourses(responseData.courses || []);
                setQuizzes(responseData.quiz || []);
                setSurveys(responseData.survey || []);
            } catch (err) {
                setError("Unable to load training data right now.");
                setCourses([]);
                setQuizzes([]);
                setSurveys([]);
            } finally {
                setLoading(false);
            }
        };

        fetchTrainingData();
    }, [userId]);

    const hasAnyData = courses.length > 0 || quizzes.length > 0 || surveys.length > 0;

    if (loading) {
        return (
            <div className="upcoming-courses">
                <p className="training-empty">Loading training data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="upcoming-courses">
                <p className="training-empty">{error}</p>
            </div>
        );
    }

    if (!hasAnyData) {
        return (
            <div className="upcoming-courses">
                <EmptyState
                    title="No Training Assigned"
                    description={emptyStateMessage || "No training data found."}
                // description="You don't have any training courses, quizzes, or surveys assigned to your profile at the moment."
                />
            </div>
        );
    }

    const statusTabs = [
        { id: "completed", label: "Completed" },
        { id: "inprogress", label: "In Progress" },
        { id: "upcoming", label: "Upcoming" },
    ];

    return (
        <div className="upcoming-courses">
            <div className="status-tabs-container">
                {statusTabs.map((tab) => (
                    <button
                        key={tab.id}
                        className={`status-tab-button ${activeStatusTab === tab.id ? "active" : ""}`}
                        onClick={() => setActiveStatusTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="status-tab-wrapper">
                <StatusTabContent
                    courses={courses}
                    quizzes={quizzes}
                    surveys={surveys}
                    statusFilter={activeStatusTab}
                />
            </div>
        </div>
    );
}
